'use client';
import {
  Box,
  Container,
} from '@chakra-ui/react';
import cn from 'classnames';
import { useRouter } from 'next/navigation';
import { setCookie } from 'nookies';
import {
  Fragment,
  useEffect,
} from 'react';

import AwardAndPeerview from '@/app/_components/award-peerview/AwardAndPeerview';
import FancyCarousel from '@/app/_components/fancy-carousel/FancyCarousel';
import FindUsSection from '@/app/_components/find-us-section/FindUsSection';
import { COOKIE_NAME_DEALERSHIP_ID } from '@/constants/dealership/dealership.constants';
import { BlogListing } from '@/services/blog/BlogService';
import { setDealershipCookieOnUser } from '@/services/gtm.service';
import {
  DealershipProps,
  DealershipSectionType,
} from '@/types/dealership/dealership.types';
import type { BodyTypesGrouping } from '@/types/home/home.types';
import type { NewCarOverview } from '@/types/new-cars/new-cars.types';
import type { Special } from '@/types/specials/specials.types';

import BannerSection from './BannerSection';
import CTA from './CTA';
import styles from './DealershipPage.module.scss';
import MeetTheTeam from './MeetTheTeam';
import NewsFeed from './NewsFeed';
import PreOwnedSelection from './PreOwnedSelection';
import Promotions from './Promotion';
import TradeIn from './TradeIn';
import WarrantySection from './WarrantySection';



type Props = {
  featuredPromotions: Special[];
  newCars: NewCarOverview[];
  dealerInfo: DealershipProps;
  body_types: BodyTypesGrouping[];
  dealerBlogs: BlogListing[] | null;
  carouselBackground: string | null;
  defaultImage: string;
};

const DealershipPage = ({
  featuredPromotions,
  newCars,
  dealerInfo,
  body_types,
  dealerBlogs,
  carouselBackground,
  defaultImage,
}: Props) => {
  const router = useRouter();

  useEffect(() => {

    // store this current dealer as the preferred dealer
    setCookie(null, COOKIE_NAME_DEALERSHIP_ID, dealerInfo.id, {
      maxAge: process.env.NEXT_PUBLIC_DEALERSHIP_COOKIE_AGE || 1800,
      path: '/',
    });

    setDealershipCookieOnUser();

    router.refresh();
  }, []);

  const renderNode = (section: DealershipSectionType) => {
    switch (section) {
      case 'contact': {
        return (
          <FindUsSection key={section} dealerInfo={dealerInfo} />
        );
      }
      case 'dealership_description': {
        if (!dealerInfo?.description) return null;
        return (
          <Container
            key={section}
            maxW="100%"
            className={styles.mapWrapper}
            paddingX={{ base: '6', md: '14' }}
            paddingTop={{ base: '14', md: '12px' }}
            paddingBottom={{ base: '14', md: '14' }}
          >
            <Box
              maxWidth={'100%'}
              dangerouslySetInnerHTML={{ __html: dealerInfo?.description }}
              margin={'auto'}
            ></Box>
          </Container>
        );
      }
      case 'meet_the_team': {
        if (!dealerInfo?.dealership_team || !dealerInfo?.dealership_team.length)
          return null;
        return (
          <MeetTheTeam
            key={section}
            teams={dealerInfo?.dealership_team}
            defaultImage={defaultImage}
          />
        );
      }
      case 'new_vehicles_slider': {
        return (
          <FancyCarousel
            key={section}
            imageHrefBase="/new-cars"
            isDealershipPage
            carsWithOverview={newCars}
            body_types={body_types}
            new_cars_background={carouselBackground ?? ''}
          />
        );
      }
      case 'promotions': {
        if (!featuredPromotions || !featuredPromotions.length) return null;
        return (
          <Promotions
            key={section}
            featuredItems={featuredPromotions}
            dealershipName={dealerInfo?.name}
          />
        );
      }
      case 'pre_owned_call_to_action': {
        if (
          !dealerInfo?.pre_owned_call_to_action ||
          !dealerInfo?.pre_owned_call_to_action?.image
        )
          return null;
        return (
          <PreOwnedSelection
            key={section}
            pre_owned_call_to_action={dealerInfo?.pre_owned_call_to_action}
          />
        );
      }
      case 'trust': {
        const awards = dealerInfo?.awards || [];
        const warranties = dealerInfo?.warranties || [];

        return (
          <Fragment key={section}>
            {awards?.length > 0 || dealerInfo.peerview ? (
              <AwardAndPeerview
                awards={awards}
                heading={
                  dealerInfo?.dealership_awards_title.length > 0
                    ? dealerInfo?.dealership_awards_title
                    : undefined
                }
                peerview={dealerInfo.peerview}
              />
            ) : null}
            {warranties.length ? (
              <WarrantySection
                warranty_title={warranties[0]?.warranties_id?.title}
                warranty_description={
                  warranties[0]?.warranties_id?.description ?? ''
                }
                warranty_image={warranties[0]?.warranties_id?.image}
              />
            ) : null}
          </Fragment>
        );
      }
      case 'cta_section': {
        if (!dealerInfo?.call_to_actions || !dealerInfo?.call_to_actions.length)
          return null;
        return (
          <CTA key={section} call_to_actions={dealerInfo?.call_to_actions} />
        );
      }
      case 'trade_in_call_to_action': {
        if (!dealerInfo?.trade_in_call_to_action?.image) return null;
        return (
          <TradeIn
            key={section}
            trade_in_call_to_action={dealerInfo?.trade_in_call_to_action}
          />
        );
      }
      case 'news_feed': {
        if (!dealerBlogs || !dealerBlogs.length) return null;
        return <NewsFeed key={section} dealerBlogs={dealerBlogs} />;
      }
      default: {
        return null;
      }
    }
  };

  return (
    <>
      <Box
        className={cn(
          styles.dealershipHeroBanner,
          dealerInfo?.header_call_to_actions.length === 0 &&
            styles.dealershipHeroBannerNoCTA,
        )}
      >
        {dealerInfo?.name && dealerInfo?.mainImage && (
          <BannerSection
            dealerInfo={dealerInfo}
            name={dealerInfo?.name}
            mainImage={dealerInfo?.mainImage ? dealerInfo?.mainImage : ''}
          />
        )}
      </Box>
      {dealerInfo?.section_order?.length > 0 &&
        dealerInfo?.section_order?.map(({ section }) => renderNode(section))}
    </>
  );
};

export default DealershipPage;
